import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ComplianceSettingForm } from 'src/app/classes/form-groups/compliance-setting-form';
import { Company, PrimaryTrade } from 'src/app/classes/models';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';
import { UtilityService } from 'src/app/services/utility.service';

type riskLevelMapObj = {
  riskLevel1: {
    complianceSettingId: number | null;
    checked: boolean;
  };
  riskLevel2: {
    complianceSettingId: number | null;
    checked: boolean;
  };
  riskLevel3: {
    complianceSettingId: number | null;
    checked: boolean;
  };
};

@Component({
  selector: 'app-compliance-trade-specific-compliance-tiers',
  templateUrl: './compliance-trade-specific-compliance-tiers.component.html',
  styleUrls: ['./compliance-trade-specific-compliance-tiers.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceTradeSpecificComplianceTiersComponent implements OnChanges {
  constructor(
    private TradeService: TradeService,
    private ToastService: ToastService,
    private UtilityService: UtilityService
  ) {}

  @Input() complianceSettingForms?: ComplianceSettingForm[];
  @Input() complianceSettings?: ComplianceSetting[];
  @Input() enableRiskLevel2: boolean = false;
  @Input() enableRiskLevel3: boolean = false;

  trades: PrimaryTrade[] = [];
  tradeRiskLevelMap: Map<string, riskLevelMapObj> = new Map([]);
  checkBoxFields?: Map<string, { controlName: string; show: boolean }>;

  collapse: boolean = true;

  keyValuePairDefaultOrder(
    a: KeyValue<any, any>,
    b: KeyValue<any, any>
  ): number {
    return 0;
  }

  updateTradeSpecificComplianceSettingId(
    tradeName: string,
    complianceSettingId: number | null
  ) {
    if (!this.complianceSettings || !this.complianceSettingForms) return;

    const complianceSetting = this.complianceSettings.find((setting) => {
      return setting.TradeSpecificCompliances?.some(
        (tsc) => tsc.PrimaryTrade?.name === tradeName
      );
    });

    if (complianceSetting) {
      const tradeSpecificCompliance =
        complianceSetting.TradeSpecificCompliances?.find(
          (tsc) => tsc.PrimaryTrade?.name === tradeName
        );

      if (tradeSpecificCompliance && complianceSettingId) {
        tradeSpecificCompliance.ComplianceSettingId = complianceSettingId;
      }

      const complianceSettingForm = this.complianceSettingForms.find(
        (form) => form.value.id === complianceSetting.id
      );

      if (complianceSettingForm) {
        complianceSettingForm.patchValue({
          TradeSpecificCompliances: complianceSetting.TradeSpecificCompliances
        });
      }
    }
  }

  onRiskLevelClick(
    newComplianceSettingId: number | null,
    trade: KeyValue<string, riskLevelMapObj>
  ) {
    const currentTrade = this.tradeRiskLevelMap.get(trade.key);

    if (currentTrade) {
      currentTrade.riskLevel1.checked = false;
      currentTrade.riskLevel2.checked = false;
      currentTrade.riskLevel3.checked = false;

      if (
        newComplianceSettingId === currentTrade.riskLevel1.complianceSettingId
      ) {
        currentTrade.riskLevel1.checked = true;
      } else if (
        newComplianceSettingId === currentTrade.riskLevel2.complianceSettingId
      ) {
        currentTrade.riskLevel2.checked = true;
      } else if (
        newComplianceSettingId === currentTrade.riskLevel3.complianceSettingId
      ) {
        currentTrade.riskLevel3.checked = true;
      }

      this.tradeRiskLevelMap.set(trade.key, currentTrade);
    }

    this.updateTradeSpecificComplianceSettingId(
      trade.key,
      newComplianceSettingId
    );
  }

  setRiskLevelSettings() {
    if (this.trades.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot set risk level settings. No trades found.',
        { returnError: true }
      );
    }

    this.trades.forEach((trade) => {
      if (!this.complianceSettings) {
        throw this.ToastService.addErrorToast(
          'Error',
          'No compliance settings.',
          { returnError: true }
        );
      }

      const { name } = trade;

      if (name) {
        const riskLevel1Setting = this.complianceSettings.find(
          (setting) => setting.riskLevel === 1
        );
        const riskLevel2Setting = this.complianceSettings.find(
          (setting) => setting.riskLevel === 2
        );
        const riskLevel3Setting = this.complianceSettings.find(
          (setting) => setting.riskLevel === 3
        );

        const currentTradeSpecificComplianceTier =
          this.complianceSettings?.find((setting) => {
            return setting.TradeSpecificCompliances?.some(
              (tsc) => tsc.PrimaryTrade?.name === name
            )
              ? setting
              : undefined;
          });

        if (!currentTradeSpecificComplianceTier) {
          this.tradeRiskLevelMap.set(name, {
            riskLevel1: {
              complianceSettingId:
                riskLevel1Setting && riskLevel1Setting.id
                  ? riskLevel1Setting.id
                  : null,
              checked: true
            },
            riskLevel2: {
              complianceSettingId:
                riskLevel2Setting && riskLevel2Setting.id
                  ? riskLevel2Setting.id
                  : null,
              checked: false
            },
            riskLevel3: {
              complianceSettingId:
                riskLevel3Setting && riskLevel3Setting.id
                  ? riskLevel3Setting.id
                  : null,
              checked: false
            }
          });
        } else {
          this.tradeRiskLevelMap.set(name, {
            riskLevel1: {
              complianceSettingId:
                riskLevel1Setting && riskLevel1Setting.id
                  ? riskLevel1Setting.id
                  : null,
              checked: riskLevel1Setting === currentTradeSpecificComplianceTier
            },
            riskLevel2: {
              complianceSettingId:
                riskLevel2Setting && riskLevel2Setting.id
                  ? riskLevel2Setting.id
                  : null,
              checked: riskLevel2Setting === currentTradeSpecificComplianceTier
            },
            riskLevel3: {
              complianceSettingId:
                riskLevel3Setting && riskLevel3Setting.id
                  ? riskLevel3Setting.id
                  : null,
              checked: riskLevel3Setting === currentTradeSpecificComplianceTier
            }
          });
        }
      }
    });
  }

  getTrades() {
    this.TradeService.getTrades(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['name']
      })
    ).subscribe({
      next: (Trades: PrimaryTrade[]) => {
        this.trades = Trades.sort((a, b) => {
          if (!a.name) return 1;
          if (!b.name) return -1;
          return a.name.localeCompare(b.name);
        });

        this.setRiskLevelSettings();
      }
    });
  }

  ngOnInit(): void {
    this.getTrades();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { complianceSettings } = changes;

    if (complianceSettings && this.trades.length > 0) {
      this.setRiskLevelSettings();
    }
  }
}
